import {
  AlertIcon,
  Avatar,
  Box,
  BuildingIcon,
  Center,
  FiEye,
  FiLogOut,
  Flex,
  IconButton,
  Menu,
  MenuButton,
  MenuDivider,
  MenuItem,
  MenuList,
  Modal,
  Navigation,
  Spinner,
  Tag,
  Text,
  Tooltip,
} from '@agrotoken-tech/ui';
import { ExplorerLogo } from '@bcba/brand';
import { useTranslation } from '@bcba/react-hooks';
import { useBreakpointValue } from '@chakra-ui/react';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { useUserContext } from '../../contexts/UserContext';
import { paths } from '../../routes/paths';
import useHeader from './hooks/useHeader';

const Header = () => {
  const {
    fullName,
    email,
    companyName,
    handleSignOut,
    links,
    currentLinkId,
    navigateTo,
    cancelAlertPendingChangeModal,
    confirmAlertPendingChangeModal,
    isPendingChangeAlertOpen,
  } = useHeader();
  const {
    user: { availableQueries },
    isSignUpRequestApproved,
  } = useUserContext();
  const { t } = useTranslation();

  const { explorerEnableProfile, explorerEnableAvailableQueries } = useFlags();

  const userData = (
    <Flex direction="column" px={2} rowGap={1}>
      <Text color="content-secondary" size="sm" variant="regular">
        {fullName}
      </Text>
      <Text color="content-tertiary" size="sm" variant="regular">
        {email}
      </Text>
      <Flex gap={2} alignItems="center">
        <BuildingIcon color="content-tertiary" />
        <Text color="content-tertiary" size="sm" variant="regular">
          {companyName}
        </Text>
      </Flex>
      {isSignUpRequestApproved && explorerEnableAvailableQueries ? (
        <Flex gap={2} alignItems="center">
          <FiEye color="#718096" />
          <Text color="content-tertiary" size="sm" variant="regular">
            {t('explorer.navigation.menu.availableQueries')}: {availableQueries}
          </Text>
        </Flex>
      ) : null}
    </Flex>
  );

  const shouldHideQueriesTag = useBreakpointValue({
    base: true,
    sm: true,
    md: true,
    lg: false,
    xl: false,
  });

  const rightElement = (
    <Flex
      justifyContent={{ base: 'start', md: 'end' }}
      alignItems="center"
      w="100%"
      gap={4}
      ml={{ base: 2, md: 0 }}
    >
      {!shouldHideQueriesTag &&
      isSignUpRequestApproved &&
      explorerEnableAvailableQueries ? (
        <Flex>
          <Tooltip
            label={t('explorer.navigation.availableQueries.tooltip')}
            hasArrow
            placement="bottom"
            bg="white"
            w="200px"
            color="content-secondary"
            size="sm"
          >
            <Tag>
              <Flex gap={2} alignItems="center">
                <FiEye color="#2d3748" />
                <Text color="content-secondary" size="sm" variant="regular">
                  {t('explorer.navigation.availableQueries.label')}{' '}
                  {availableQueries}
                </Text>
              </Flex>
            </Tag>
          </Tooltip>
        </Flex>
      ) : null}
      <Box display={{ base: 'none', md: 'flex' }}>
        <Menu>
          <MenuButton
            as={IconButton}
            icon={<Avatar name={fullName} />}
            borderRadius="full"
            borderWidth={0}
          />
          <MenuList px={2}>
            {userData}
            <MenuDivider />
            {explorerEnableProfile && (
              <>
                <MenuItem onClick={() => navigateTo(paths.profile)}>
                  <Text size="sm">{t('explorer.navigation.menu.profile')}</Text>
                </MenuItem>
                <MenuDivider />
              </>
            )}
            <MenuItem icon={<FiLogOut />} onClick={() => handleSignOut()}>
              <Text size="sm">{t('common.logout')}</Text>
            </MenuItem>
          </MenuList>
        </Menu>
      </Box>
      <Box bg="white" display={{ base: 'flex', md: 'none' }}>
        {userData}
      </Box>
    </Flex>
  );

  if (currentLinkId === null)
    return (
      <Center mt="8">
        <Spinner />
      </Center>
    );

  return (
    <Box minHeight="min-content">
      <Navigation
        homePath={paths.home}
        links={links}
        mainButton={<ExplorerLogo />}
        rightElement={rightElement}
        onClickLogout={handleSignOut}
        logoutLabel={t('common.logout')}
        defaultIndex={currentLinkId}
        overrideIndex={currentLinkId}
      />
      <Modal
        isOpen={isPendingChangeAlertOpen}
        onClose={cancelAlertPendingChangeModal}
        title={t('explorer.signUp.modal.exitForm.title')}
        text={t('explorer.signUp.modal.exitForm.text')}
        onClickConfirm={confirmAlertPendingChangeModal}
        labelBtnConfirm={t('common.confirm')}
        variantBtnConfirm="primary"
        labelBtnClose={t('common.cancel')}
        icon={AlertIcon}
        iconVariant="warning"
      />
    </Box>
  );
};

export default Header;
